/**
 * The one definition of "normalise a source SVG for use as a UI icon" (#216).
 *
 * Two paths render the same glyphs and MUST agree:
 *
 *  - the `<!-- svg:path/to/icon.svg -->` marker in `index.html`, expanded at
 *    build / request time by `inlineSvgPlaceholders` in `vite.config.ts`;
 *  - the runtime `import icon from "./icon.svg?raw"` constants, for the places
 *    an icon is genuinely dynamic (a per-mode sub-tool button, a layers chip,
 *    a tool's `icon` field).
 *
 * They used to normalise separately, which is a fork waiting to happen: the
 * static button and the chip drawing the same glyph could disagree about paint
 * or size, and neither the build nor the type checker would say a word.
 *
 * ## What it does, and why an icon has to be written for it
 *
 * The preamble (`<?xml …?>`, editor comments) is dropped, and `width`,
 * `height`, `fill` and `stroke` ATTRIBUTES are stripped — so an icon inherits
 * its container's `currentColor` and whatever size CSS gives it, rather than
 * carrying an 800px hard-coded black.
 *
 * The strip is GLOBAL, not just on the root element, and that is the trap:
 *
 *  - paint that must survive belongs in a `style` attribute, not in `fill=` /
 *    `stroke=` (an outline icon written with `fill="none"` on the root comes out
 *    FILLED once the attribute is gone, since the sub-toolbar CSS sets
 *    `fill: currentColor`);
 *  - a `<rect>` written with `width` / `height` attributes loses its dimensions
 *    and never draws at all.
 *
 * Neither mistake fails the build, and both have shipped. `e2e/icon-sizes.spec.ts`
 * measures what actually rendered, which is the only check that catches them.
 */

/** Strip an icon SVG down to what a UI button should inherit. Returns "" when
 *  the input holds no `<svg>` element at all. */
export function stripIconSvg(raw: string): string {
  const start = raw.indexOf("<svg");
  if (start < 0) return "";
  return raw.slice(start).replace(/\s(width|height|fill|stroke)="[^"]*"/g, "");
}
